import React, { useRef, useState } from 'react';
import { Tooltip, IconButton } from '@mui/material';
import { cx } from '@emotion/css';
import { Autocomplete, Dashed } from '@/components';

import { Close as CloseIcon } from '@mui/icons-material';

import type { ChildNewModel } from '@/hooks/useProfile';
import { GradeChangeInput, Grade } from '@/pages/AccountInformation/AccountInformation.types';
import {
  schoolItemFromChildInstitution,
  type SchoolAutocompleteItem,
} from '@/utils/schoolAutocomplete';
import infoIcon from '@/assets/info-icon.svg';
import { useGoogleTranslator } from '@/hooks/useGoogleTranslator';
import {
  BodySmall,
  BodyMediumMidEmphasis,
  ButtonFlatSmall,
  ButtonPrimarySmall,
  BodyLarge,
} from '@/design-system';
import colors from '@/theme/colors';
import { SelectInput } from '../SelectInput/SelectInput';
import ChildRemovalConfirmation from '../ChildRemovalConfirmation/ChildRemovalConfirmation';
import styles from './ChildEnrollment.styles';
import { GRADE_REQUIRED_MESSAGE, SCHOOL_NAME_REQUIRED_MESSAGE } from '@/config/constants';

const toolTipText =
  'Enter the school your child or children attend. Only schools that are using Peachjar are available for selection.';

const currentYear = new Date().getFullYear();
const nextYear = currentYear + 1;

type SchoolInstitution = Pick<
  ChildNewModel,
  'institutionId' | 'institutionName' | 'hierarchyId' | 'hierarchyName'
>;

type ChildEnrollmentProps = {
  id: string;
  childUid: string;
  childIndex: number;
  disabled: boolean;
  wasAdded?: boolean;
  wasChanged?: boolean;
  institution: SchoolInstitution;
  currentGrade: Grade | undefined;
  grades: GradeChangeInput[];
  lookUpSchools: (options: any, callback: any) => void;
  gradeLevelChanged: boolean;
  handleSubmit: (childUId: string) => Promise<void> | void;
  handleSchoolChange: (value: any, id: any) => void;
  handleGradeChange: (input: any, childUId: string) => void;
  handleRemoveChild: (childUId: string) => Promise<void> | void;
  handleCancel: (childUId: string) => void;
  focusAddChildButton: () => void;
};

const ChildEnrollment: React.FC<ChildEnrollmentProps> = ({
  id,
  childUid,
  childIndex,
  disabled,
  wasAdded,
  wasChanged,
  institution,
  currentGrade,
  grades,
  lookUpSchools,
  gradeLevelChanged,
  handleSubmit,
  handleSchoolChange,
  handleGradeChange,
  handleRemoveChild,
  handleCancel,
  focusAddChildButton,
}) => {
  const [isRemovalModalOpen, setIsRemovalModalOpen] = useState<boolean>(false);
  const [hasSchoolError, setHasSchoolError] = useState<boolean>(false);
  const [hasGradeError, setHasGradeError] = useState<boolean>(false);
  const removeChildButtonRef = useRef<HTMLButtonElement | null>(null);
  const toolTipTextTranslated = useGoogleTranslator(toolTipText);
  const schoolNameLabel = useGoogleTranslator('School Name');
  const gradeLabel = useGoogleTranslator(`Grade (${currentYear}/${nextYear} school year)`);
  const isFormInvalid = institution.institutionName === '' || !currentGrade;
  const schoolTooltipDescId = `school-tooltip-desc-${id}`;

  const selectedSchoolValue = schoolItemFromChildInstitution(institution);

  const closeRemovalModalAndRestoreFocus = (): void => {
    setIsRemovalModalOpen(false);
    window.requestAnimationFrame(() => {
      if (removeChildButtonRef.current) {
        removeChildButtonRef.current.focus();
      } else {
        focusAddChildButton();
      }
    });
  };

  const onSubmitRemovalModal = async (): Promise<void> => {
    setIsRemovalModalOpen(false);
    await handleRemoveChild(childUid);
  };

  const onClickButtonSubmit = () => {
    if (institution.institutionName === '') {
      setHasSchoolError(true);
    }
    if (!currentGrade) {
      setHasGradeError(true);
    }
    if (isFormInvalid) {
      return;
    }

    handleSubmit(childUid);
  };

  return (
    <>
      <div className={styles.cardBorder} />
      <fieldset id={id} tabIndex={-1} className={styles.enrollmentGroup} data-testid="childCard" data-child-uid={childUid}>
        <div
          className={cx(
            styles.innerContainer,
            hasSchoolError || hasGradeError ? styles.hasError : {}
          )}
        >
          <div className={styles.formSectionHeadline}>
            <legend className={styles.legend}>
              <BodyMediumMidEmphasis>
                {wasAdded ? 'New Child' : `Child ${childIndex}`}
              </BodyMediumMidEmphasis>
            </legend>
            {!disabled && (
              <div className={`${styles.removeIcon} ${styles.tooltipButton}`}>
                <Tooltip
                  title="Remove Child"
                  placement="right"
                  enterTouchDelay={0}
                  data-testid="removeChildTooltip"
                >
                  <IconButton
                    ref={removeChildButtonRef}
                    aria-label="Remove Child"
                    data-testid="removeChildButton"
                    onClick={() => {
                      if (wasAdded) {
                        handleRemoveChild(childUid);
                      } else {
                        setIsRemovalModalOpen(true);
                      }
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </div>
            )}
          </div>
          <div className={cx(styles.inputWithToolTip, styles.inlined)}>
            {disabled ? (
              <div>
                <BodySmall
                  as="label"
                  id={`schoolLabel${id}`}
                  className={styles.label}
                  color={colors.slate}
                >
                  School Name
                </BodySmall>
                <BodyLarge
                  aria-labelledby={`schoolLabel${id}`}
                  className={styles.textInput}
                  data-testid="schoolNameLabel"
                  color={colors.prussian}
                >
                  {institution.institutionName}
                </BodyLarge>
              </div>
            ) : (
              <div className={cx(styles.autocomplete, hasSchoolError && styles.inputSchoolError)}>
                <span id={schoolTooltipDescId} className="sr-only">
                  {toolTipTextTranslated}
                </span>
                <Autocomplete<SchoolAutocompleteItem>
                  id={`schoolInput${id}`}
                  lookup={lookUpSchools}
                  label={schoolNameLabel}
                  aria-label={`${schoolNameLabel}, Child ${childIndex}`}
                  value={selectedSchoolValue}
                  isEqual={(s1, s2) => {
                    return (s1 && s2 && s1.schoolId === s2.schoolId) || false;
                  }}
                  onChange={val => {
                    if (val) {
                      setHasSchoolError(false);
                    }
                    handleSchoolChange(val, childUid);
                  }}
                  renderOption={val => (
                    <div data-testid="schoolNameOption">
                      {val.schoolName}
                      <span className={styles.secondaryName} data-testid="districtNameOption">
                        {val.districtName}
                      </span>
                    </div>
                  )}
                  error={hasSchoolError ? SCHOOL_NAME_REQUIRED_MESSAGE : null}
                  data-testid="schoolAutocompleteInput"
                  required
                  aria-describedby={schoolTooltipDescId}
                />
              </div>
            )}

            {disabled ? (
              <div className={styles.labelGrade}>
                <BodySmall
                  as="label"
                  id={`gradeLabel${id}`}
                  color={colors.slate}
                  className={styles.label}
                  data-testid="gradeLabel"
                >
                  {gradeLabel}
                </BodySmall>
                <BodyLarge
                  aria-labelledby={`gradeLabel${id}`}
                  color={colors.slate}
                  className={styles.textInput}
                >
                  {!currentGrade || currentGrade.description === 'N' ? (
                    <Dashed data-testid="grade" />
                  ) : (
                    <span data-testid="grade">{currentGrade.description}</span>
                  )}
                </BodyLarge>
              </div>
            ) : (
              <div
                className={cx(
                  styles.inputWithToolTip,
                  styles.enrollmentGroup,
                  styles.gradeOptions,
                  hasGradeError && styles.inputGradeError
                )}
              >
                <SelectInput
                  childUid={childUid}
                  value={currentGrade}
                  label={gradeLabel}
                  required
                  onChange={input => {
                    setHasGradeError(false);
                    handleGradeChange(input, childUid);
                  }}
                  options={grades}
                  error={hasGradeError}
                  errorMessage={hasGradeError ? GRADE_REQUIRED_MESSAGE : undefined}
                />
              </div>
            )}
          </div>
          {gradeLevelChanged && (
            <span className={`${styles.infoIcon}`} data-testid="gradeLevelChangedIcon">
              <img src={infoIcon} alt="" />
            </span>
          )}

          {(wasAdded || wasChanged) && (
            <div className={styles.footer}>
              <div>
                <ButtonFlatSmall
                  onClick={() => handleCancel(childUid)}
                  data-testid="childCardCancelButton"
                  fontColor={colors.shadesJungle800}
                >
                  Cancel
                </ButtonFlatSmall>
              </div>
              <div onClick={onClickButtonSubmit}>
                <ButtonPrimarySmall disabled={isFormInvalid} data-testid="childCardSubmitButton">
                  Save
                </ButtonPrimarySmall>
              </div>
            </div>
          )}
        </div>
      </fieldset>
      <ChildRemovalConfirmation
        isOpen={isRemovalModalOpen}
        schoolName={institution.institutionName}
        onSubmit={onSubmitRemovalModal}
        onCancel={closeRemovalModalAndRestoreFocus}
      />
    </>
  );
};

export default ChildEnrollment;
